/**
 * CTA Dialog Content Configuration
 * Contains different CTA slide sets for various conversion scenarios
 */

import { pluginUrl } from '../lib/utils';
import { CTAContent } from '../types/components';

export const CTA_CONTENT: Record<string, CTAContent> = {
  quota_exceeded: {
    type: 'quota_exceeded',
    slides: [
      {
        id: 'unlimited-summaries-slide',
        imageUrl: `${pluginUrl}/assets/images/repurpose-image.jpg`,
        title: 'Unlimited Summaries',
        description:
          'Get unlimited access to all summarizer tools. Process PDFs, YouTube videos, web pages, podcasts, and more without daily limits. Perfect for researchers, students, and professionals.',
        ctaText: 'GET FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
        features: ['summary', 'summarize', 'default'],
      },
      {
        id: 'ai-chat-slide',
        imageUrl: `${pluginUrl}/assets/images/chat-image.jpg`,
        title: 'AI Chat & Analysis',
        description:
          'Chat with your content using advanced AI. Ask follow-up questions, get detailed explanations, and dive deeper into any topic. Transform static content into interactive knowledge.',
        ctaText: 'GET FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
        features: ['chat', 'ai-chat'],
      },
      {
        id: 'repurpose-slide',
        imageUrl: `${pluginUrl}/assets/images/collection-image.jpg`,
        title: 'Content Repurposing',
        description:
          'Transform your summaries into blog posts, social media content, study guides, and more. Our AI-powered tools help you create fresh content from existing materials with custom prompts.',
        ctaText: 'GET FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
        features: ['repurpose', 'repurposing'],
      },
      {
        id: 'translation-slide',
        imageUrl: `${pluginUrl}/assets/images/notes-composition.jpg`,
        title: 'Multi-Language Translation',
        description:
          'Get your summaries translated into multiple languages instantly. Perfect for international research, multilingual content creation, and global communication.',
        ctaText: 'GET FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
        features: ['translation', 'translate'],
      },
      {
        id: 'collections-slide',
        imageUrl: `${pluginUrl}/assets/images/collections-frame.jpg`,
        title: 'Organize Content in Smart Collections',
        description:
          'Create personalized collections to organize your summaries by topic, project, or purpose. Build your knowledge library and access insights whenever you need them.',
        ctaText: 'CREATE ACCOUNT',
        ctaUrl: 'https://web.tubeonai.com/login',
        features: ['collections'],
      },
      {
        id: 'premium-features-slide',
        imageUrl: `${pluginUrl}/assets/images/notes-composition.jpg`,
        title: 'Premium Features',
        description:
          'Access advanced features like custom prompts, multiple export formats, team collaboration, and priority processing. Get the most out of your content analysis workflow.',
        ctaText: 'GET FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
        features: ['premium'],
      },
    ],
    // Feature to slide mappings
    featureSlideMap: {
      'summary': 'unlimited-summaries-slide',
      'summarize': 'unlimited-summaries-slide',
      'chat': 'ai-chat-slide',
      'ai-chat': 'ai-chat-slide',
      'repurpose': 'repurpose-slide',
      'repurposing': 'repurpose-slide',
      'translation': 'translation-slide',
      'translate': 'translation-slide',
      'collections': 'collections-slide',
      'premium': 'premium-features-slide',
    },
  },
  premium_features: {
    type: 'premium_features',
    slides: [
      {
        id: 'premium-features-slide',
        imageUrl: `${pluginUrl}/assets/images/notes-composition.jpg`,
        title: 'Premium Features',
        description:
          'Access advanced features like custom prompts, multiple export formats, team collaboration, and priority processing. Get the most out of your content analysis workflow.',
        ctaText: 'GET FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
        features: ['premium', 'default'],
      },
      {
        id: 'collections-slide',
        imageUrl: `${pluginUrl}/assets/images/collection-image.jpg`,
        title: 'Collections',
        description:
          'Organize, Chat, and Repurpose insights from your favorite content with ease. Build personal libraries of knowledge, ask questions, and even transform the knowledge into fresh content.',
        ctaText: 'GET FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
        features: ['collections'],
      },
      {
        id: 'unlimited-summaries-slide',
        imageUrl: `${pluginUrl}/assets/images/repurpose-image.jpg`,
        title: 'Unlimited Summaries',
        description:
          'Get unlimited access to all summarizer tools. Process PDFs, YouTube videos, web pages, podcasts, and more without daily limits. Perfect for researchers, students, and professionals.',
        ctaText: 'GET FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
        features: ['summary', 'summarize'],
      },
      {
        id: 'ai-chat-slide',
        imageUrl: `${pluginUrl}/assets/images/chat-image.jpg`,
        title: 'AI Chat & Analysis',
        description:
          'Chat with your content using advanced AI. Ask follow-up questions, get detailed explanations, and dive deeper into any topic. Transform static content into interactive knowledge.',
        ctaText: 'GET FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
        features: ['chat', 'ai-chat'],
      },
    ],
    featureSlideMap: {
      'premium': 'premium-features-slide',
      'collections': 'collections-slide',
      'summary': 'unlimited-summaries-slide',
      'summarize': 'unlimited-summaries-slide',
      'chat': 'ai-chat-slide',
      'ai-chat': 'ai-chat-slide',
    },
  },
  sign_up_required: {
    type: 'sign_up_required',
    slides: [
      {
        id: 'youtube-summarizer-slide',
        imageUrl: `${pluginUrl}/assets/images/summarize-youtube-video.jpg`,
        title: 'Summarize YouTube Videos in Seconds',
        description:
          'Transform lengthy YouTube videos into concise summaries instantly. Save hours of viewing time and get key insights from educational content, tutorials, and presentations.',
        ctaText: 'CREATE FREE ACCOUNT',
        ctaUrl: 'https://web.tubeonai.com/login',
      },
      {
        id: 'podcast-summary-slide',
        imageUrl: `${pluginUrl}/assets/images/enjoy-podcast-summary.jpg`,
        title: 'Enjoy Podcast Summaries On The Go',
        description:
          'Never miss insights from your favorite podcasts. Get instant summaries of any podcast episode with our AI-powered platform and stay informed while saving time.',
        ctaText: 'START FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
      },
      {
        id: 'transcript-slide',
        imageUrl: `${pluginUrl}/assets/images/transcript-frame.jpg`,
        title: 'Get Timestamped Transcripts Instantly',
        description:
          'Access accurate, timestamped transcripts for any video content. Navigate to specific sections, search for keywords, and reference content with precise timestamps.',
        ctaText: 'TRY IT FREE',
        ctaUrl: 'https://web.tubeonai.com/login',
      },
      {
        id: 'channels-slide',
        imageUrl: `${pluginUrl}/assets/images/channels-frame.jpg`,
        title: 'Subscribe to Your Favorite Channels',
        description:
          'Follow YouTube channels and get automatic summaries when new videos are published. Stay updated with your favorite creators without watching every video.',
        ctaText: 'GET STARTED',
        ctaUrl: 'https://web.tubeonai.com/login',
      },
      {
        id: 'collections-slide',
        imageUrl: `${pluginUrl}/assets/images/collections-frame.jpg`,
        title: 'Organize Content in Smart Collections',
        description:
          'Create personalized collections to organize your summaries by topic, project, or purpose. Build your knowledge library and access insights whenever you need them.',
        ctaText: 'CREATE ACCOUNT',
        ctaUrl: 'https://web.tubeonai.com/login',
      },
      {
        id: 'repurpose-slide',
        imageUrl: `${pluginUrl}/assets/images/repurpose-frame.jpg`,
        title: 'Repurpose Content with AI',
        description:
          'Transform video content into blog posts, social media posts, study guides, and more. Our AI-powered prompt library helps you create new content from existing materials.',
        ctaText: 'START FREE TRIAL',
        ctaUrl: 'https://web.tubeonai.com/login?next=/pricing',
      },
    ],
  },
};

// Default CTA content (fallback)
export const DEFAULT_CTA_CONTENT: CTAContent = CTA_CONTENT.quota_exceeded;

// Helper function to get CTA content by type
export const getCTAContent = (type: string): CTAContent => {
  return CTA_CONTENT[type] || DEFAULT_CTA_CONTENT;
};

// Helper function to get initial slide index based on feature context
export const getInitialSlideIndex = (
  content: CTAContent,
  featureContext?: string
): number => {
  if (!featureContext || !content.featureSlideMap) {
    return 0; // Default to first slide
  }

  // Find the slide ID for this feature
  const targetSlideId = content.featureSlideMap[featureContext];
  if (!targetSlideId) {
    return 0; // Default if no mapping found
  }

  // Find the index of this slide in the slides array
  const slideIndex = content.slides.findIndex(slide => slide.id === targetSlideId);
  return slideIndex !== -1 ? slideIndex : 0;
};
